import type { ChatMessage } from "@/types/chat";

type MessageLike = Partial<ChatMessage> & {
  id?: string | number | null;
  sender_id?: string | number | null;
  chat_room_id?: string | number | null;
  created_at?: string | Date | null;
  updated_at?: string | Date | null;
};

const TEMP_ID_PREFIX = "temp-";

const messageId = (message: MessageLike) =>
  message.id === null || message.id === undefined ? "" : String(message.id);

const isTempMessage = (message: MessageLike) =>
  messageId(message).startsWith(TEMP_ID_PREFIX);

const numericId = (message: MessageLike) => {
  const id = messageId(message);
  if (!id || id.startsWith(TEMP_ID_PREFIX)) return Number.MAX_SAFE_INTEGER;
  const parsed = Number(id);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

const messageTime = (message: MessageLike) => {
  const value = message.created_at;
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sameValue = (
  left?: string | number | null,
  right?: string | number | null,
) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const bothEmpty = (
  left?: string | number | null,
  right?: string | number | null,
) =>
  (left === undefined || left === null || left === "") &&
  (right === undefined || right === null || right === "");

const hasSameRealId = (left: MessageLike, right: MessageLike) => {
  const leftId = messageId(left);
  const rightId = messageId(right);
  return (
    !!leftId &&
    !!rightId &&
    !leftId.startsWith(TEMP_ID_PREFIX) &&
    leftId === rightId
  );
};

const isLikelyConfirmationForTemp = (
  tempMessage: MessageLike,
  serverMessage: MessageLike,
) => {
  if (!isTempMessage(tempMessage)) return false;
  if (
    tempMessage.sender_id &&
    serverMessage.sender_id &&
    !sameValue(tempMessage.sender_id, serverMessage.sender_id)
  ) {
    return false;
  }
  if (
    tempMessage.chat_room_id &&
    serverMessage.chat_room_id &&
    !sameValue(tempMessage.chat_room_id, serverMessage.chat_room_id)
  ) {
    return false;
  }

  const tempContent = String(tempMessage.content ?? "");
  const serverContent = String(serverMessage.content ?? "");
  const sameReply =
    bothEmpty(tempMessage.reply_to_id, serverMessage.reply_to_id) ||
    sameValue(tempMessage.reply_to_id, serverMessage.reply_to_id);

  return tempContent === serverContent && sameReply;
};

export const sortChatMessages = <T extends MessageLike>(messages: T[]): T[] =>
  messages
    .map((message, index) => ({ message, index }))
    .sort((left, right) => {
      const timeDiff = messageTime(left.message) - messageTime(right.message);
      if (timeDiff !== 0) return timeDiff;

      const idDiff = numericId(left.message) - numericId(right.message);
      if (idDiff !== 0) return idDiff;

      return left.index - right.index;
    })
    .map(({ message }) => message);

export const mergeChatMessages = <T extends MessageLike>(
  previousMessages: T[],
  incomingMessages: T[],
): T[] => {
  const merged: T[] = [];

  [...previousMessages, ...incomingMessages].forEach((message) => {
    const id = messageId(message);
    const existingIndex =
      id && !id.startsWith(TEMP_ID_PREFIX)
        ? merged.findIndex((item) => messageId(item) === id)
        : -1;

    if (existingIndex >= 0) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...message,
      };
      return;
    }

    merged.push(message);
  });

  return sortChatMessages(merged);
};

export const upsertRealtimeMessage = <T extends MessageLike>(
  previousMessages: T[],
  incomingMessage: T,
): T[] => {
  if (
    previousMessages.some((message) =>
      hasSameRealId(message, incomingMessage),
    )
  ) {
    return mergeChatMessages(previousMessages, [incomingMessage]);
  }

  const tempIndex = previousMessages.findIndex((message) =>
    isLikelyConfirmationForTemp(message, incomingMessage),
  );

  if (tempIndex >= 0) {
    const nextMessages = [...previousMessages];
    nextMessages[tempIndex] = incomingMessage;
    return mergeChatMessages([], nextMessages);
  }

  return mergeChatMessages(previousMessages, [incomingMessage]);
};

export const confirmSentMessage = <T extends MessageLike>(
  previousMessages: T[],
  tempId: string,
  confirmedMessage: T,
): T[] => {
  const tempIndex = previousMessages.findIndex(
    (message) => messageId(message) === tempId,
  );

  if (tempIndex >= 0) {
    const nextMessages = [...previousMessages];
    nextMessages[tempIndex] = confirmedMessage;
    return mergeChatMessages([], nextMessages);
  }

  return upsertRealtimeMessage(previousMessages, confirmedMessage);
};

export const withoutTempMessage = <T extends MessageLike>(
  messages: T[],
  tempId: string,
): T[] => messages.filter((message) => messageId(message) !== tempId);
